import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  signal,
  computed,
  inject,
  OnChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
} from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { TextareaModule } from 'primeng/textarea';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  DataTableComponent,
  TableColumn,
} from '@shared/components/data-table/data-table.component';
import {
  PropertyFormCategoryData,
  PropertyValue,
  getFormTypeOptions,
  getSourceTypeOptions,
  getDisplayGroupOptions,
  normalizeDisplayGroup,
} from '../../models/classify.config';

@Component({
  selector: 'app-property-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    TranslateModule,
    CheckboxModule,
    ButtonModule,
    DividerModule,
    TextareaModule,
    ConfirmationDialogComponent,
    DataTableComponent,
    AppButtonComponent,
  ],
  templateUrl: './property-dialog.component.html',
  styleUrls: ['./property-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyDialogComponent implements OnChanges {
  @Input() visible = false;
  @Input() initialData: PropertyFormCategoryData | null = null;
  @Input() viewMode = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() confirm = new EventEmitter<PropertyFormCategoryData>();
  private readonly translateService = inject(TranslateService);
  propertyForm!: FormGroup;
  propertyValues = signal<PropertyValue[]>([]);
  selectedValues = signal<PropertyValue[]>([]);
  showValueTable = signal(false);
  validationError = signal<string>(''); // Signal to track validation errors

  // Confirmation dialog state
  showConfirmationDialog = signal(false);
  confirmationMessage = signal('');
  confirmationButtons = signal<ConfirmationButton[]>([]);

  // Computed property to check if any values are unsaved
  hasUnsavedValues = computed(() => {
    return this.propertyValues().some((v) => !v.isSaved);
  });

  formTypeOptions: any[] = [];
  sourceTypeOptions: any[] = [];
  displayGroupOptions: any[] = [];

  valueTableColumns: TableColumn[] = [
    {
      label: this.translateService.instant(
        'goods.classify.dialog.property.value_table.value',
      ),
      prop: 'value',
      type: 'custom',
      width: 100,
    },
    {
      label: this.translateService.instant(
        'goods.classify.dialog.property.value_table.actions',
      ),
      prop: 'actions',
      type: 'actions',
      width: 180,
    },
  ];

  constructor(private readonly fb: FormBuilder) {
    this.initializeForm();
    this.formTypeOptions = getFormTypeOptions(this.translateService);
    this.sourceTypeOptions = getSourceTypeOptions(this.translateService);
    this.displayGroupOptions = getDisplayGroupOptions(this.translateService);
  }

  private initializeForm(): void {
    this.propertyForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      code: ['', [Validators.required, Validators.maxLength(255)]],
      formType: ['', Validators.required], // Default to empty string, required
      skuFlag: [false],
      statusFlag: [true],
      remark: ['', Validators.maxLength(255)],
      sourceType: [2], // Default to Category Attribute
      displayGroup: ['OTHER'],
    });

    // Watch for form type changes
    this.propertyForm.get('formType')?.valueChanges.subscribe((value) => {
      this.onFormTypeChange(); // Call the new method
    });
  }

  // Watch for initialData changes to populate form when editing
  ngOnChanges(): void {
    if (this.initialData && this.propertyForm?.get('formType')) {
      this.populateForm(this.initialData);
      this.handleViewMode();
    }
  }

  private handleViewMode(): void {
    if (this.propertyForm) {
      if (this.viewMode) {
        this.propertyForm.disable();
      } else {
        this.propertyForm.enable();
      }
    }
  }

  private populateForm(data: PropertyFormCategoryData): void {
    this.propertyForm.patchValue({
      name: data.name,
      code: data.code,
      formType: data.formType,
      skuFlag: data.skuFlag,
      statusFlag: data.statusFlag,
      remark: data.remark,
      sourceType: data.sourceType,
      displayGroup: normalizeDisplayGroup(data.displayGroup),
    });

    // Set property values
    if (data.values && data.values.length > 0) {
      this.propertyValues.set(
        data.values.map((v) => ({
          ...v,
          isSaved: true,
          isEditing: false,
          originalValue: v.value,
          wasOriginallySaved: true, // These are existing saved rows
        })),
      );
    } else {
      this.propertyValues.set([]);
    }

    // Use setTimeout to ensure form is fully updated before checking form type
    setTimeout(() => {
      this.onFormTypeChange();
    }, 100);
  }

  onFormTypeChange(): void {
    const formType = this.propertyForm.get('formType')?.value;
    // Only show value table if formType is not empty and is one of the types that need values
    this.showValueTable.set(formType !== '' && [1, 2, 4].includes(formType));

    if (this.showValueTable()) {
      this.propertyForm.get('skuFlag')?.enable();
    } else {
      this.propertyForm.get('skuFlag')?.disable();
    }
  }

  onAddValue(): void {
    const newValue: PropertyValue = {
      id: Date.now() + Math.random(), // Ensure unique ID to prevent reference conflicts
      value: '', // Ensure it starts empty
      sort: this.propertyValues().length + 1,
      isSaved: false, // New values start as unsaved
      isEditing: true, // New values start in editing mode
      originalValue: '', // Store original empty value
      wasOriginallySaved: false, // This is a new row, never saved
    };
    this.propertyValues.set([...this.propertyValues(), newValue]);

    // Clear validation error when user clicks Add button
    this.validationError.set('');
  }

  onDeleteValue(id: number): void {
    const currentValues = this.propertyValues();
    const filteredValues = currentValues.filter((v) => v.id !== id);
    this.propertyValues.set(filteredValues);
  }

  onDeleteSelectedValues(): void {
    const selectedCount = this.selectedValues().length;

    this.confirmationMessage.set(
      this.translateService.instant(
        'goods.classify.dialog.property.confirmation_dialog.message',
        { count: selectedCount },
      ),
    );
    this.confirmationButtons.set([
      {
        label: this.translateService.instant(
          'goods.classify.dialog.property.confirmation_dialog.cancel',
        ),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant(
          'goods.classify.dialog.property.confirmation_dialog.delete',
        ),
        action: 'confirm',
        severity: 'danger',
      },
    ]);
    this.showConfirmationDialog.set(true);
  }

  onConfirmationDialogAction(action: string): void {
    this.showConfirmationDialog.set(false);

    if (action === 'confirm') {
      // User confirmed deletion
      const currentValues = this.propertyValues();
      const selectedIds = new Set(this.selectedValues().map((v) => v.id));
      const filteredValues = currentValues.filter(
        (v) => !selectedIds.has(v.id),
      );
      this.propertyValues.set(filteredValues);
      this.selectedValues.set([]); // Clear selection after deletion
    }
    // If action is 'cancel', do nothing
  }

  onInputChange(value: PropertyValue, event: Event): void {
    const target = event.target as HTMLInputElement;
    const newValue = target.value;

    // Update the value directly without triggering signal updates
    value.value = newValue;
    value.isSaved = false;
    value.isEditing = true;

    // Don't update the signal immediately to prevent focus loss
    // The signal will be updated when Save/Cancel buttons are clicked
  }

  private updatePropertyValue(id: number, newValue: string): void {
    const currentValues = this.propertyValues();
    const updatedValues = currentValues.map((v) =>
      v.id === id
        ? { ...v, value: newValue, isSaved: false, isEditing: true }
        : v,
    );
    this.propertyValues.set(updatedValues);
  }

  onValueModelChange(value: PropertyValue): void {
    // This method is called when ngModel changes
    const currentValues = this.propertyValues();
    const updatedValues = currentValues.map((v) =>
      v.id === value.id ? { ...v, isSaved: false, isEditing: true } : v,
    );
    this.propertyValues.set(updatedValues);
  }

  onValueSelectionChange(selection: PropertyValue[]): void {
    this.selectedValues.set(selection);
  }

  // New methods for save/edit functionality
  onSaveValue(id: number): void {
    // First update the signal with the current input value
    const currentValues = this.propertyValues();
    const valueToSave = currentValues.find((v) => v.id === id);
    if (valueToSave) {
      this.updatePropertyValue(id, valueToSave.value);
    }

    // Then mark only the specific value as saved and not editing
    const updatedValues = currentValues.map((v) =>
      v.id === id
        ? { ...v, isSaved: true, isEditing: false, wasOriginallySaved: true }
        : v,
    );
    this.propertyValues.set(updatedValues);
    this.validationError.set(''); // Clear error on successful save
  }

  onEditValue(id: number): void {
    // Mark only the specific value as editing and store original value
    const currentValues = this.propertyValues();
    const updatedValues = currentValues.map((v) =>
      v.id === id ? { ...v, isEditing: true, originalValue: v.value } : v,
    );
    this.propertyValues.set(updatedValues);
  }

  onCancelEdit(id: number): void {
    // Mark only the specific value as not editing and restore original value
    const currentValues = this.propertyValues();
    const valueToCancel = currentValues.find((v) => v.id === id);

    if (valueToCancel) {
      // If this was originally a new row (never saved), remove it completely when cancel is clicked
      if (valueToCancel.wasOriginallySaved) {
        // Otherwise, restore the original value and exit edit mode
        const updatedValues = currentValues.map((v) =>
          v.id === id
            ? { ...v, isEditing: false, value: v.originalValue || v.value }
            : v,
        );
        this.propertyValues.set(updatedValues);
      } else {
        this.onDeleteValue(id);
      }
    }
  }

  maxLengthParams(labelKey: string, requiredLength: number): Record<string, unknown> {
    return { fieldName: this.translateService.instant(labelKey), requiredLength };
  }

  onCancel(): void {
    this.visibleChange.emit(false);
    this.resetForm();
  }

  onConfirm(): void {
    // Check if form is valid
    if (!this.propertyForm.valid) {
      this.markFormGroupTouched();
      return;
    }

    // Check if property values are required but not provided
    const formType = this.propertyForm.get('formType')?.value;
    const requiresValues = [1, 2, 4].includes(formType); // Single Choice, Checkbox, Dropdown

    if (requiresValues && this.propertyValues().length === 0) {
      this.validationError.set(
        this.translateService.instant(
          'goods.classify.dialog.property.validation.values_required',
        ),
      );
      return;
    }

    // Check if any property values are empty or invalid
    const hasInvalidValues = this.propertyValues().some(
      (value) => !value.value || value.value.trim() === '',
    );
    if (hasInvalidValues) {
      this.validationError.set(
        this.translateService.instant(
          'goods.classify.dialog.property.validation.values_incomplete',
        ),
      );
      return;
    }

    // Clear any validation errors since form is valid
    this.validationError.set('');

    const formData: PropertyFormCategoryData = {
      ...this.propertyForm.value,
      displayGroup: normalizeDisplayGroup(this.propertyForm.value.displayGroup),
      values: this.propertyValues(),
    };
    this.confirm.emit(formData);
    this.onCancel();
  }

  /**
   * Mark all form controls as touched to trigger validation display
   */
  private markFormGroupTouched(): void {
    Object.keys(this.propertyForm.controls).forEach((key) => {
      const control = this.propertyForm.get(key);
      control?.markAsTouched();
    });
  }

  private resetForm(): void {
    this.propertyForm.reset();
    this.propertyValues.set([]);
    this.selectedValues.set([]);
    this.showValueTable.set(false);
    this.validationError.set(''); // Clear validation error on reset

    // Reset form controls to default state
    this.propertyForm.patchValue({
      name: '',
      code: '',
      formType: '', // Reset to empty string
      skuFlag: false,
      statusFlag: true,
      remark: '',
      sourceType: 2,
      displayGroup: 'OTHER',
    });

    // Re-enable generateSku control
    this.propertyForm.get('skuFlag')?.enable();
  }
}
